import { api } from './api';

export type TaskStatus =
  | 'pending'
  | 'in_progress'
  | 'awaiting_barrister'
  | 'awaiting_clerk'
  | 'hearing_followup'
  | 'filing_pending'
  | 'blocked'
  | 'completed'
  | 'cancelled';

export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface Task {
  id: string;
  chambersId: string;
  caseId: string | null;
  parentTaskId: string | null;
  hearingId: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assignedToId: string | null;
  createdById: string;
  completedById: string | null;
  completionNote: string | null;
  dueDate: string | null;
  completedAt: string | null;
  estimatedMinutes: number | null;
  actualMinutes: number | null;
  isPrivate: boolean;
  createdAt: string;
  updatedAt: string;
  case: { id: string; caseNumber: string; caseName: string } | null;
  assignedTo: { id: string; firstName: string; lastName: string; memberType: string } | null;
  createdBy: { id: string; firstName: string; lastName: string };
  completedBy: { id: string; firstName: string; lastName: string; memberType: string } | null;
  hearing: { id: string; hearingDate: string; hearingType: string; courtId: string | null } | null;
}

export interface ListTasksQuery {
  page?: number;
  limit?: number;
  search?: string;
  caseId?: string;
  assignedToId?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  sortBy?: 'dueDate' | 'createdAt' | 'priority' | 'status' | 'completedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface ListTasksResponse {
  tasks: Task[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export async function fetchTasks(query: ListTasksQuery = {}): Promise<ListTasksResponse> {
  const res = await api.get<{ data: ListTasksResponse }>('/tasks', { params: query });
  return res.data.data;
}

export async function fetchTask(id: string): Promise<Task> {
  const res = await api.get<{ data: Task }>(`/tasks/${id}`);
  return res.data.data;
}

export interface CreateTaskPayload {
  title: string;
  description?: string;
  caseId?: string;
  hearingId?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  assignedToId?: string;
  dueDate?: string;
  estimatedMinutes?: number;
  isPrivate?: boolean;
}

export interface CompleteTaskPayload {
  status: 'completed';
  completionNote?: string;
}

export async function createTask(payload: CreateTaskPayload): Promise<Task> {
  const res = await api.post<{ data: Task }>('/tasks', payload);
  return res.data.data;
}

export async function updateTask(id: string, payload: Partial<CreateTaskPayload> & { status?: TaskStatus; completionNote?: string }): Promise<Task> {
  const res = await api.patch<{ data: Task }>(`/tasks/${id}`, payload);
  return res.data.data;
}

export async function deleteTask(id: string): Promise<void> {
  await api.delete(`/tasks/${id}`);
}

export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  awaiting_barrister: 'Awaiting barrister',
  awaiting_clerk: 'Awaiting clerk',
  hearing_followup: 'Hearing follow-up',
  filing_pending: 'Filing pending',
  blocked: 'Blocked',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const ACTIVE_STATUSES: TaskStatus[] = [
  'pending',
  'in_progress',
  'awaiting_barrister',
  'awaiting_clerk',
  'hearing_followup',
  'filing_pending',
  'blocked',
];

export const STATUS_WEIGHT: Record<TaskStatus, number> = {
  blocked: 0,
  in_progress: 1,
  awaiting_barrister: 2,
  awaiting_clerk: 3,
  hearing_followup: 4,
  filing_pending: 5,
  pending: 6,
  completed: 7,
  cancelled: 8,
};

export function parseTimeEstimate(raw: string): number | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(parseFloat(s) * 60);
  if (/^\d+(\.\d+)?h$/.test(s)) return Math.round(parseFloat(s) * 60);
  if (/^\d+m$/.test(s)) return parseInt(s.replace('m', ''), 10);
  const colon = s.match(/^(\d+):(\d{1,2})$/);
  if (colon) return parseInt(colon[1]!, 10) * 60 + parseInt(colon[2]!, 10);
  const hm = s.match(/^(\d+)h\s*(\d+)m$/);
  if (hm) return parseInt(hm[1]!, 10) * 60 + parseInt(hm[2]!, 10);
  return null;
}

export function formatTimeEstimate(minutes: number | null): string {
  if (minutes == null || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
